import {
  ApplicantProgress,
  ApplicantStepKey,
  ApplicantStepState,
  ApplicationProgress
} from "api/applications";
import { ApplicantStatus, ApplicantType } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { Application } from "types/application";
import { getApplicantRole } from "utils/getApplicantRole";
import { LEGEND_ORDER } from "./constants";

export type PersonProgress = {
  key: string;
  applicantId?: number;
  name: string;
  firstName?: string;
  roleLabel: string;
  invited: boolean;
  status: ApplicantStatus;
  steps: Record<ApplicantStepKey, ApplicantStepState> | null;
  lastActivityAt: string | null;
};

const MS_PER_DAY = 86_400_000;

const inviteeSteps = (
  role: ApplicantType
): Record<ApplicantStepKey, ApplicantStepState> => ({
  rental_application: "todo",
  housing_history: role === ApplicantType.CoSigner ? "na" : "todo",
  identity_verification: "todo",
  income_verification: role === ApplicantType.Occupant ? "na" : "todo",
  checkout: "todo"
});

const resolvePrimaryApplicantId = (
  application: Application,
  progress?: ApplicationProgress
) =>
  progress?.primary_applicant_id ??
  application.applicants.find((a) => a.role === ApplicantType.Applicant)?.id;

export const buildPeople = (
  application: Application,
  progress?: ApplicationProgress
): PersonProgress[] => {
  const progressByApplicantId = new Map<number, ApplicantProgress>(
    (progress?.applicants ?? []).map((entry) => [entry.id, entry])
  );
  const primaryApplicantId = resolvePrimaryApplicantId(application, progress);
  const hasApplicantRole = application.applicants.some(
    (a) => a.role === ApplicantType.Applicant
  );

  const applicants = application.applicants.map((applicant) => {
    const entry = progressByApplicantId.get(applicant.id);
    return {
      key: `a-${applicant.id}`,
      applicantId: applicant.id,
      name:
        getFullName({
          firstName: applicant.first_name ?? "",
          lastName: applicant.last_name ?? ""
        }).trim() || applicant.email,
      firstName: applicant.first_name ?? undefined,
      roleLabel: getApplicantRole(
        applicant.role,
        applicant.id === primaryApplicantId
      ),
      invited: false,
      status: applicant.application_status,
      steps: entry?.steps ?? null,
      lastActivityAt: entry?.last_activity_at ?? null
    };
  });

  const invitees = (application.pending_magic_links ?? []).map((invitee) => ({
    key: `i-${invitee.email}`,
    name: invitee.email ?? "",
    roleLabel: getApplicantRole(
      invitee.role,
      hasApplicantRole ? false : undefined
    ),
    invited: true,
    status: ApplicantStatus.Pending,
    steps: inviteeSteps(invitee.role),
    lastActivityAt: null
  }));

  return [...applicants, ...invitees];
};

export const householdProgress = (people: PersonProgress[]) => {
  const applicable = people.flatMap((person) =>
    Object.values(person.steps ?? {}).filter(
      (state) => state !== "na" && state !== "skipped"
    )
  );
  return {
    done: applicable.filter((state) => state === "done").length,
    total: applicable.length
  };
};

export const legendStates = (people: PersonProgress[]) =>
  LEGEND_ORDER.filter((state) =>
    people.some((person) => Object.values(person.steps ?? {}).includes(state))
  );

export const lastActivityLabel = (person: PersonProgress, now: number) => {
  if (person.invited) return "Not opened";
  if (!person.lastActivityAt) return "Not started";
  const days = Math.floor(
    (now - Date.parse(person.lastActivityAt)) / MS_PER_DAY
  );
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

export const hasAllProgress = (people: PersonProgress[]) =>
  people.every((person) => person.steps !== null);
